<template>
  <div 
    v-if="isMGMAndMO ? true
    : !(status !== 'Draft' && status !== 'Verification' && data.data.length === 0)"
  >
    <div
      v-if="isMGMAndMO? (!hasData && !search && status !== 'Finished') 
      : (!hasData && !search)"
    >
      <div class="clearfix mb-4">
        <h4 class="card-title">{{ $t('maintenance.order.serviceFeeList') }}</h4>
      </div>
      <b-card>
        <McEmptyState
          :title="$t('maintenance.order.emptyState2')"
          :desc="$t('maintenance.order.emptyStateText2')"
          @buttonPrimaryAction="addHandler"
          placementType="grid"
        ></McEmptyState>
      </b-card>
    </div>

    <ServiceTable
      ref="services-table"
      :status="status"
      :apiClient="query"
      :fields="fields"
      :service2SoId="+$route.params.id"
      :listDatas="servicesList"
      :addData="addData"
      v-else-if="hasData || search"
    />
  </div>
</template>
<script>
import appConfig from '@/app.config'
import McEmptyState from '@/components/McEmptyState'
import ServiceTable from './Tables/ServiceTable'

import {
  GET_LABOUR_2_SERVICE_ORDERS,
  GET_LABOUR_2_SERVICE_ORDER,
  CREATE_LABOUR_2_SERVICE_ORDER,
  DELETE_LABOUR_2_SERVICE_ORDER,
  UPDATE_LABOUR_2_SERVICE_ORDER,
} from '@/services/api/maintenance/labour2ServiceOrder'
import { Helper } from '@/mixin/helper'
import { GET_LABOURS } from '@/services/api/master/services'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    ServiceTable,
    McEmptyState,
  },
  mixins: [Helper],

  apollo: {
    labours: {
      query: GET_LABOURS,
      fetchPolicy: 'no-cache',
      result({ data, loading }) {
        if (!loading && data) this.servicesList = data.labours.labours.data
      },
    },
  },
  props: ['status'],
  data() {
    return {
      data: [],
      servicesList: [],
      hasData: true,
      addData: false,
      search: '',
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    fields() {
      return [
        {
          name: 'labour',
          key: 'labour.name',
          label: this.$t('maintenance.order.form.serviceName'),
          placeholder: `${this.$t('global.form.select')} ${this.$t(
            'maintenance.order.form.serviceName'
          )}`,
          modalTitle: this.$t('maintenance.order.form.serviceName'),
          isEditable: true,
          isSortable: true,
          validation: 'required',
          type: 'value-help-v2',
          listGetter: this.resourcefulQuery.labourOptions,
          tableFields: [
            {
              key: 'code',
              thClass: 'mc-table--50',
              label: this.$t('master.service.serviceCode'),
              sortable: true,
              isRedirectable: true,
            },
            {
              key: 'name',
              thClass: 'mc-table--50',
              label: this.$t('master.service.serviceName'),
              sortable: true,
            },
            {
              key: 'description',
              thClass: 'mc-table--50',
              label: this.$t('master.service.description'),
              sortable: true,
            },
            {
              key: 'fee',
              thClass: 'mc-table--50',
              label: this.$t('master.service.price'),
              sortable: true,
              formatter: (val) => {
                return `Rp. ${this.thousandFormat(val)}`
              },
            },
          ],
          displayedBy: 'name',
          trackBy: 'labourId',
          thStyle: 'width: 500px',
        },
        {
          name: 'qty',
          key: 'qty',
          label: this.$t('maintenance.order.form.qty'),
          placeholder: this.$t('maintenance.order.form.qty'),
          isEditable: true,
          isSortable: false,
          validation: 'required|number',
        },
        {
          name: 'fee',
          key: 'fee',
          label: this.$t('dashboard.text.costAmount'),
          placeholder: this.$t('dashboard.text.costAmount'),
          isEditable: true,
          isSortable: false,
          validation: 'number',
          formatter: (val) => {
            return `Rp ${this.thousandFormat(val)}`
          },
        },
      ]
    },

    query() {
      return {
        get: async (filter) => await this.getData(filter),
        getById: async (labourId) => {
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_LABOUR_2_SERVICE_ORDER,
            variables: {
              filter: {
                labourId: +labourId,
                serviceOrderId: Number(this.$route.params.id),
              },
            },
          })

          return res.data.labour2ServiceOrder
        },
        store: async (data) => {
          const res = await this.$apollo.mutate({
            mutation: CREATE_LABOUR_2_SERVICE_ORDER,
            variables: {
              input: {
                labourId: +data.labour,
                qty: Number(data.qty),
                fee: Number(data.fee),
                serviceOrderId: +this.$route.params.id,
              },
            },
          })

          if (res.data.createLabour2ServiceOrder.isSuccessful) {
            const self = this
            self.$emit('update-total-price')

            return res.data.createLabour2ServiceOrder
          }
        },
        update: async (labourId, oldData, newData) => {
          const res = await this.$apollo.mutate({
            mutation: UPDATE_LABOUR_2_SERVICE_ORDER,
            variables: {
              input: {
                oldData: {
                  qty: +oldData.qty,
                  fee: +oldData.fee,
                },
                newData: {
                  qty: +newData.qty,
                  fee: +newData.fee,
                },
                labourId: +labourId,
                serviceOrderId: +this.$route.params.id,
              },
            },
          })

          if (res.data.updateLabour2ServiceOrder.isSuccessful) {
            const self = this
            self.$emit('update-total-price')

            return res.data.updateLabour2ServiceOrder
          }
        },
        delete: async (labourId) => {
          const input = {
            labourId: Number(labourId),
            serviceOrderId: Number(this.$route.params.id),
          }
          const res = await this.$apollo.mutate({
            fetchPolicy: 'no-cache',
            mutation: DELETE_LABOUR_2_SERVICE_ORDER,
            variables: {
              input: input,
            },
          })

          if (res.data.deleteLabour2ServiceOrder.isSuccessful) {
            const self = this
            self.$emit('update-total-price')

            return res.data.deleteLabour2ServiceOrder
          }
        },
      }
    },

    resourcefulQuery() {
      return {
        labourOptions: async (opts) => {
          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
            excludeLabourId: this.data.data.map((item) =>
              typeof item === 'number' ? item : item.labourId
            ),
          }

          const res = await this.$apollo.query({
            query: GET_LABOURS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          return {
            metadata: {
              total_count: res.data.labours.labours.pagination.total,
              page: res.data.labours.labours.pagination.page,
            },
            data: {
              paginated_result: res.data.labours.labours.data,
            },
          }
        },
      }
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    addHandler() {
      this.addData = true
      this.hasData = true
    },

    async getData(opts) {
      this.search = opts?.search

      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.show,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_LABOUR_2_SERVICE_ORDERS,
        variables: {
          filter: { ...filter, serviceOrderId: Number(this.$route.params.id) },
        },
      })

      const self = this
      self.data = res.data.labour2ServiceOrders.labour2ServiceOrders

      if (!self.addData) {
        if (self.data.data.length !== 0) self.hasData = true
        else self.hasData = false
      }
      return self.data
    },
  },
}
</script>
